<template>
  <div
    class="relative select-none mb-4 before:border before:rounded before:top-0 before:bottom-0 before:left-0 before:right-0 before:absolute"
    :class="{ 'cursor-crosshair': allowDraw && editable, 'touch-none': !!drawField }"
    style="container-type: size"
    :style="{ aspectRatio: `${width} / ${height}`}"
    @click.self="selectedAreaRef.value = null"
  >
    <img
      ref="image"
      loading="lazy"
      :src="image.url"
      :width="width"
      :height="height"
      class="rounded"
      @load="onImageLoad"
    >
    <div
      class="top-0 bottom-0 left-0 right-0 absolute"
      @pointerdown="onStartDraw"
    >
      <FieldArea
        v-for="(item, i) in areas"
        :key="i"
        :ref="setAreaRefs"
        :area="item.area"
        :input-mode="inputMode"
        :page-width="width"
        :page-height="height"
        :field="item.field"
        :editable="editable"
        :with-field-placeholder="withFieldPlaceholder"
        :with-signature-id="withSignatureId"
        :with-prefillable="withPrefillable"
        :default-field="defaultFieldsIndex[item.field.name]"
        :default-submitters="defaultSubmitters"
        :max-page="totalPages - 1"
        @start-resize="resizeDirection = $event"
        @stop-resize="resizeDirection = null"
        @remove="$emit('remove-area', item.area)"
        @scroll-to="$emit('scroll-to', $event)"
      />
      <FieldArea
        v-if="newArea"
        :is-draw="true"
        :page-width="width"
        :page-height="height"
        :field="{ submitter_uuid: selectedSubmitter.uuid, type: drawField?.type || dragFieldPlaceholder?.type || defaultFieldType }"
        :area="newArea"
      />
    </div>
    <div
      v-show="resizeDirection || isDrag || showMask || (drawField && isMobile) || fieldsDragFieldRef.value"
      id="mask"
      ref="mask"
      class="top-0 bottom-0 left-0 right-0 absolute"
      :class="{ 'z-10': !isMobile, 'cursor-grab': isDrag, 'cursor-nwse-resize': drawField, [resizeDirectionClasses[resizeDirection]]: !!resizeDirectionClasses }"
      @pointermove="onPointermove"
      @pointerdown="onStartDraw"
      @dragover.prevent="onDragover"
      @dragenter="onDragenter"
      @dragleave="newArea = null"
      @drop="onDrop"
      @pointerup="onPointerup"
    />
    <div
      v-if="selectionRect && selectionRect.w > 0.005"
      class="absolute pointer-events-none border-2 border-purple-500 bg-purple-100 bg-opacity-30 z-20"
      :style="{
        left: (selectionRect.x * 100) + '%',
        top: (selectionRect.y * 100) + '%',
        width: (selectionRect.w * 100) + '%',
        height: (selectionRect.h * 100) + '%'
      }"
    />
    <div
      v-if="copyRect && copyRect.w > 0.005"
      class="absolute border-2 border-green-500 bg-green-100 bg-opacity-30 z-50"
      :style="{
        left: (copyRect.x * 100) + '%',
        top: (copyRect.y * 100) + '%',
        width: (copyRect.w * 100) + '%',
        height: (copyRect.h * 100) + '%',
        cursor: copyRectMode === 'move' ? 'grab' : 'default'
      }"
      @mousedown.stop.prevent="clearCopyRect"
    />
    <div
      v-if="alignmentRect && alignmentRect.w > 0.005"
      class="absolute pointer-events-none border-2 border-blue-500 bg-blue-100 bg-opacity-30 z-20"
      :style="{
        left: (alignmentRect.x * 100) + '%',
        top: (alignmentRect.y * 100) + '%',
        width: (alignmentRect.w * 100) + '%',
        height: (alignmentRect.h * 100) + '%'
      }"
    />
  </div>
</template>

<script>
import FieldArea from './area'

export default {
  name: 'TemplatePage',
  components: {
    FieldArea
  },
  inject: ['fieldTypes', 'defaultDrawFieldType', 'fieldsDragFieldRef', 'assignDropAreaSize', 'selectedFieldUuids', 'duplicatedFieldUuids', 'alignmentFieldUuids', 'selectedAreaRef'],
  props: {
    image: {
      type: Object,
      required: true
    },
    dragFieldPlaceholder: {
      type: Object,
      required: false,
      default: null
    },
    withSignatureId: {
      type: Boolean,
      required: false,
      default: null
    },
    withPrefillable: {
      type: Boolean,
      required: false,
      default: false
    },
    areas: {
      type: Array,
      required: false,
      default: () => []
    },
    inputMode: {
      type: Boolean,
      required: false,
      default: false
    },
    defaultFields: {
      type: Array,
      required: false,
      default: () => []
    },
    withFieldPlaceholder: {
      type: Boolean,
      required: false,
      default: false
    },
    totalPages: {
      type: Number,
      required: true
    },
    drawFieldType: {
      type: String,
      required: false,
      default: ''
    },
    allowDraw: {
      type: Boolean,
      required: false,
      default: true
    },
    selectedSubmitter: {
      type: Object,
      required: true
    },
    defaultSubmitters: {
      type: Array,
      required: false,
      default: () => []
    },
    drawField: {
      type: Object,
      required: false,
      default: null
    },
    editable: {
      type: Boolean,
      required: false,
      default: true
    },
    isDrag: {
      type: Boolean,
      required: false,
      default: false
    },
    number: {
      type: Number,
      required: true
    }
  },
  emits: ['draw', 'drop-field', 'remove-area', 'scroll-to', 'select-fields', 'duplicate-fields', 'move-duplicated-fields'],
  data () {
    return {
      areaRefs: [],
      showMask: false,
      resizeDirection: null,
      newArea: null,
      selectionRect: null,
      copyRect: null,
      alignmentRect: null
    }
  },
  computed: {
    defaultFieldsIndex () {
      return this.defaultFields.reduce((acc, field) => {
        acc[field.name] = field

        return acc
      }, {})
    },
    defaultFieldType () {
      if (this.drawFieldType) {
        return this.drawFieldType
      } else if (this.defaultDrawFieldType && this.defaultDrawFieldType !== 'text') {
        return this.defaultDrawFieldType
      } else if (this.fieldTypes.length !== 0 && !this.fieldTypes.includes('text')) {
        return this.fieldTypes[0]
      } else {
        return 'text'
      }
    },
    isMobile () {
      const isMobileSafariIos = 'ontouchstart' in window && navigator.maxTouchPoints > 0 && /AppleWebKit/i.test(navigator.userAgent)

      return isMobileSafariIos || /android|iphone|ipad/i.test(navigator.userAgent)
    },
    resizeDirectionClasses () {
      return {
        nwse: 'cursor-nwse-resize',
        nesw: 'cursor-nesw-resize',
        ew: 'cursor-ew-resize'
      }
    },
    width () {
      return this.image.metadata.width
    },
    height () {
      return this.image.metadata.height
    }
  },
  beforeUpdate () {
    this.areaRefs = []
  },
  methods: {
    startCopyRectDrag (e) {
      if (this.copyRectMode !== 'move') return
      this.isDraggingCopyRect = true
      this.copyRectDragFrom = {
        mouseX: e.clientX,
        mouseY: e.clientY,
        rectX: this.copyRect.x,
        rectY: this.copyRect.y
      }
      const startRectX = this.copyRect.x
      const startRectY = this.copyRect.y
      let prevDx = 0
      let prevDy = 0
      const onMouseMove = (ev) => {
        if (!this.copyRectDragFrom) return
        const mask = this.$refs.mask
        if (!mask) return
        const rect = mask.getBoundingClientRect()
        const totalDx = (ev.clientX - this.copyRectDragFrom.mouseX) / rect.width
        const totalDy = (ev.clientY - this.copyRectDragFrom.mouseY) / rect.height
        const dx = totalDx - prevDx
        const dy = totalDy - prevDy
        prevDx = totalDx
        prevDy = totalDy
        this.copyRect.x = startRectX + totalDx
        this.copyRect.y = startRectY + totalDy
        this.$emit('move-duplicated-fields', { dx, dy, uuids: this.duplicatedUuids })
      }
      const onMouseUp = () => {
        this.copyRectDragFrom = null
        this.isDraggingCopyRect = false
        document.removeEventListener('mousemove', onMouseMove)
        document.removeEventListener('mouseup', onMouseUp)
      }
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', onMouseUp)
    },
    clearCopyRect () {
      this.copyRect = null
      this.copyRectMode = null
      this.duplicatedUuids = []
      this.copyRectDragFrom = null
      if (this.duplicatedFieldUuids) {
        this.duplicatedFieldUuids.value = new Set()
      }
    },
    onImageLoad (e) {
      this.image.metadata.width = e.target.naturalWidth
      this.image.metadata.height = e.target.naturalHeight
    },
    setAreaRefs (el) {
      if (el) {
        this.areaRefs.push(el)
      }
    },
    onDragenter (e) {
      this.newArea = {}

      this.assignDropAreaSize(this.newArea, this.dragFieldPlaceholder, {
        maskW: this.$refs.mask.clientWidth,
        maskH: this.$refs.mask.clientHeight
      })

      this.newArea.x = (e.offsetX - 6) / this.$refs.mask.clientWidth
      this.newArea.y = e.offsetY / this.$refs.mask.clientHeight - this.newArea.h / 2
    },
    onDragover (e) {
      this.newArea.x = (e.offsetX - 6) / this.$refs.mask.clientWidth
      this.newArea.y = e.offsetY / this.$refs.mask.clientHeight - this.newArea.h / 2
    },
    onDrop (e) {
      this.newArea = null

      this.$emit('drop-field', {
        x: e.offsetX,
        y: e.offsetY,
        maskW: this.$refs.mask.clientWidth,
        maskH: this.$refs.mask.clientHeight,
        page: this.number
      })
    },
    onStartDraw (e) {
      if (!this.editable) return
      if ((e.ctrlKey || e.metaKey) && e.shiftKey) {
        this.showMask = true
        this.$nextTick(() => {
          this.copyRect = {
            initialX: e.offsetX / this.$refs.mask.clientWidth,
            initialY: e.offsetY / this.$refs.mask.clientHeight,
            x: e.offsetX / this.$refs.mask.clientWidth,
            y: e.offsetY / this.$refs.mask.clientHeight,
            w: 0,
            h: 0
          }
        })
        return
      }
      if (e.shiftKey && !e.ctrlKey && !e.metaKey) {
        this.showMask = true
        this.$nextTick(() => {
          this.alignmentRect = {
            initialX: e.offsetX / this.$refs.mask.clientWidth,
            initialY: e.offsetY / this.$refs.mask.clientHeight,
            x: e.offsetX / this.$refs.mask.clientWidth,
            y: e.offsetY / this.$refs.mask.clientHeight,
            w: 0,
            h: 0
          }
        })
        return
      }
      if (e.ctrlKey || e.metaKey) {
        this.showMask = true
        this.$nextTick(() => {
          this.selectionRect = {
            initialX: e.offsetX / this.$refs.mask.clientWidth,
            initialY: e.offsetY / this.$refs.mask.clientHeight,
            x: e.offsetX / this.$refs.mask.clientWidth,
            y: e.offsetY / this.$refs.mask.clientHeight,
            w: 0,
            h: 0
          }
        })
        return
      }
      if (!this.allowDraw) {
        return
      }

      if (this.isMobile && !this.drawField) {
        return
      }

      if (!this.editable) {
        return
      }

      this.showMask = true

      this.$nextTick(() => {
        this.newArea = {
          initialX: e.offsetX / this.$refs.mask.clientWidth,
          initialY: e.offsetY / this.$refs.mask.clientHeight,
          x: e.offsetX / this.$refs.mask.clientWidth,
          y: e.offsetY / this.$refs.mask.clientHeight,
          w: 0,
          h: 0
        }
      })
    },
    onPointermove (e) {
      if (this.copyRect) {
        const dx = e.offsetX / this.$refs.mask.clientWidth - this.copyRect.initialX
        const dy = e.offsetY / this.$refs.mask.clientHeight - this.copyRect.initialY
        this.copyRect.x = dx > 0 ? this.copyRect.initialX : e.offsetX / this.$refs.mask.clientWidth
        this.copyRect.y = dy > 0 ? this.copyRect.initialY : e.offsetY / this.$refs.mask.clientHeight
        this.copyRect.w = Math.abs(dx)
        this.copyRect.h = Math.abs(dy)
      }
      if (this.alignmentRect) {
        const dx = e.offsetX / this.$refs.mask.clientWidth - this.alignmentRect.initialX
        const dy = e.offsetY / this.$refs.mask.clientHeight - this.alignmentRect.initialY
        this.alignmentRect.x = dx > 0 ? this.alignmentRect.initialX : e.offsetX / this.$refs.mask.clientWidth
        this.alignmentRect.y = dy > 0 ? this.alignmentRect.initialY : e.offsetY / this.$refs.mask.clientHeight
        this.alignmentRect.w = Math.abs(dx)
        this.alignmentRect.h = Math.abs(dy)
        return
      }
      if (this.selectionRect) {
        const dx = e.offsetX / this.$refs.mask.clientWidth - this.selectionRect.initialX
        const dy = e.offsetY / this.$refs.mask.clientHeight - this.selectionRect.initialY
        this.selectionRect.x = dx > 0 ? this.selectionRect.initialX : e.offsetX / this.$refs.mask.clientWidth
        this.selectionRect.y = dy > 0 ? this.selectionRect.initialY : e.offsetY / this.$refs.mask.clientHeight
        this.selectionRect.w = Math.abs(dx)
        this.selectionRect.h = Math.abs(dy)
        return
      }
      if (this.newArea) {
        const dx = e.offsetX / this.$refs.mask.clientWidth - this.newArea.initialX
        const dy = e.offsetY / this.$refs.mask.clientHeight - this.newArea.initialY

        if (dx > 0) {
          this.newArea.x = this.newArea.initialX
        } else {
          this.newArea.x = e.offsetX / this.$refs.mask.clientWidth
        }

        if (dy > 0) {
          this.newArea.y = this.newArea.initialY
        } else {
          this.newArea.y = e.offsetY / this.$refs.mask.clientHeight
        }

        if ((this.drawField?.type || this.drawFieldType) === 'cells') {
          this.newArea.cell_w = this.newArea.h * (this.$refs.mask.clientHeight / this.$refs.mask.clientWidth)
        }

        this.newArea.w = Math.abs(dx)
        this.newArea.h = Math.abs(dy)
      }
    },
    onPointerup (e) {
      if (this.duplicatedFieldUuids && this.duplicatedFieldUuids.value && this.duplicatedFieldUuids.value.size > 0) {
        this.duplicatedFieldUuids.value = new Set()
      }
      if (this.copyRectMode === 'move' && !this.isDraggingCopyRect) {
        this.clearCopyRect()
        return
      }
      if (this.copyRect) {
        const rect = this.copyRect
        const matched = this.areas.filter(item => {
          const a = item.area
          return a.x < rect.x + rect.w && a.x + a.w > rect.x &&
                 a.y < rect.y + rect.h && a.y + a.h > rect.y
        })
        if (matched.length > 0) {
          const originalUuids = matched.map(item => item.field.uuid)
          this.$emit('duplicate-fields', originalUuids, (newUuids) => {
            this.duplicatedUuids = newUuids
          })
        }
        this.copyRect = null
        this.showMask = false
        this.newArea = null
        return
      }
      if (this.alignmentRect) {
        const rect = this.alignmentRect
        const matched = this.areas.filter(item => {
          const a = item.area
          return a.x < rect.x + rect.w && a.x + a.w > rect.x &&
                 a.y < rect.y + rect.h && a.y + a.h > rect.y
        }).map(item => item.field.uuid)
        if (matched.length > 0) {
          this.alignmentFieldUuids.value = new Set(matched)
        }
        this.alignmentRect = null
        this.showMask = false
        this.newArea = null
        return
      }
      if (this.selectionRect) {
        const rect = this.selectionRect
        const matched = this.areas.filter(item => {
          const a = item.area
          return a.x < rect.x + rect.w && a.x + a.w > rect.x &&
                 a.y < rect.y + rect.h && a.y + a.h > rect.y
        }).map(item => item.field.uuid)
        if (matched.length > 0) {
          const newSet = new Set(this.selectedFieldUuids.value)
          matched.forEach(uuid => newSet.add(uuid))
          this.selectedFieldUuids.value = newSet
        }
        this.selectionRect = null
        this.showMask = false
        this.newArea = null
        return
      }
      if (this.newArea) {
        const area = {
          x: this.newArea.x,
          y: this.newArea.y,
          w: this.newArea.w,
          h: this.newArea.h,
          page: this.number
        }

        if ('cell_w' in this.newArea) {
          area.cell_w = this.newArea.cell_w
        }

        const dx = Math.abs(e.offsetX - this.$refs.mask.clientWidth * this.newArea.initialX)
        const dy = Math.abs(e.offsetY - this.$refs.mask.clientHeight * this.newArea.initialY)

        const isTooSmall = dx < 8 && dy < 8

        this.$emit('draw', { area, isTooSmall })
      }

      this.showMask = false
      this.newArea = null
    }
  }
}
</script>
